import type { FormDataProps } from "./interface";

function parseOptionalInt(s: string): number | null {
  const t = String(s).trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function parseFee(s: string): number | null {
  const t = String(s).trim();
  if (t === "") return null;
  const n = Number(t.replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function joinObs(baseObs: string, detailsObs: string): string {
  const left = baseObs.trim();
  const right = detailsObs.trim();
  if (!left) return right;
  if (!right) return left;
  return `${left}, ${right}`;
}

function buildInternationalPaymentObs(formData: FormDataProps): string {
  const details = [
    { label: "Swift Code", value: formData.swiftCode },
    { label: "Account Number/IBAN", value: formData.bankAccountNumber },
    { label: "Bank Branch Address", value: formData.internationalBankBranch },
    { label: "Amazon Gift Card Email", value: formData.amazonGiftCardEmail },
  ]
    .map(({ label, value }) => {
      const trimmed = String(value ?? "").trim();
      return trimmed ? `${label}: ${trimmed}` : "";
    })
    .filter(Boolean);

  return details.join(", ");
}

/** Corpo alinhado ao CreateInteractionDto / UpdateInteractionDto do backend. */
export function buildInteractionPayload(formData: FormDataProps) {
  const assetIds = Array.isArray(formData.assets)
    ? formData.assets
        .map((id) => Number(String(id).trim()))
        .filter((n) => Number.isFinite(n) && n > 0)
    : [];

  const asanaTrimmed = (formData.asanaProjectUrl ?? "").trim();
  const parsedExpertId = Number(String(formData.expert_id ?? "").trim());
  const hasValidExpertId =
    Number.isFinite(parsedExpertId) && parsedExpertId > 0;
  const baseObs = String(formData.obs ?? "");
  const shouldAttachInternationalObs = formData.persona === 3;
  const internationalObs = shouldAttachInternationalObs
    ? buildInternationalPaymentObs(formData)
    : "";
  const obs = joinObs(baseObs, internationalObs);

  return {
    researcherId: Number(formData.researcherId),
    clientId: Number(formData.clientId),
    project_number: (formData.projectNumber ?? "").trim(),
    asana_task_url: asanaTrimmed,
    date: formData.date,
    hour: formData.hour || "00:00",
    ...(hasValidExpertId ? { expertId: parsedExpertId } : {}),
    ...(formData.expert?.trim() ? { expert: formData.expert.trim() } : {}),
    ...(formData.expert_company?.trim()
      ? { expert_company: formData.expert_company.trim() }
      : {}),
    fee: parseFee(formData.fee),
    duration: parseOptionalInt(formData.duration),
    real_duration: parseOptionalInt(formData.real_duration),
    // status é NOT NULL no banco; sem valor assumimos "Not Done" (1)
    status: formData.status ?? 1,
    currency: formData.currency,
    delay: formData.delay,
    interaction_model: formData.interaction_model,
    interaction_type: formData.interaction_type,
    proactive: formData.proactive,
    persona: formData.persona,
    payment_method: formData.payment_method,
    feedback: formData.feedback,
    payment_deadline: formData.payment_deadline || null,
    payment_day: formData.payment_day || null,
    delay_value: parseOptionalInt(formData.delay_value),
    analysts: formData.analysts,
    obs,
    full_name: formData.full_name,
    cpf: formData.cpf,
    bank: formData.bank,
    agency: formData.agency,
    account: formData.account,
    pix: formData.pix,
    birthday: formData.birthday,
    rg: formData.rg,
    country: formData.country,
    cep: formData.cep,
    address: formData.address,
    city: formData.city,
    assetIds,
  };
}
